import React from 'react';
import cn from 'clsx';
import { ReactComponent as CommonFileSvg } from 'assets/icons/files/Common.svg';
import { ReactComponent as ReminderSvg } from 'assets/icons/New/Reminder.svg';
import { ReactComponent as DesktopReviewSvg } from 'assets/icons/Nav/DesktopReview1.svg';
import { ReactComponent as ProductTestingSvg } from 'assets/icons/Nav/ProductTesting.svg';
import { ReactComponent as QCInspectorSvg } from 'assets/icons/Nav/QCInspector.svg';
import { ReactComponent as InternalInspectionSvg } from 'assets/icons/Nav/InternalInspection.svg';
import { ReactComponent as FactorySvg } from 'assets/icons/Nav/Supplier.svg';
import { ATooltip } from 'components/Antd';
import { getDateWithFormat, getMomentDate } from 'util/date';
import { formatBytes } from 'util/file';
import T from 'i18n-react';
import classes from './Jobs.module.scss';

export const SOURCE_ICON_MAPPING = {
  productTesting: <ProductTestingSvg />,
  documentReview: <DesktopReviewSvg />,
  qcInspection: <QCInspectorSvg />,
  internalQCInspection: <InternalInspectionSvg />,
  factoryAudit: <FactorySvg />,
};

const renderDocumentName = (val) => {
  return (
    <span>
      <CommonFileSvg />
      <span>{val || '/'}</span>
    </span>
  );
};
export const getUrl = (val, module) => {
  switch (module) {
    case 'productTesting':
      return `/product-testing/${val}`;
    case 'documentReview':
      return `/document-review/${val}`;
    case 'qcInspection':
      return `/inspection/jobs/${val}`;
    case 'internalQCInspection':
      return `/inspection/internal/${val}`;
    case 'factoryAudit':
      return `/factory_audit/job/${val}`;
    default:
      return '';
  }
};
export const renderSource = (val, obj) => {
  if (obj?.isFolder) return '';
  const iconType = val?.module;
  const hasValue = !!val?.id;
  return (
    <span
      onClick={() => {
        if (hasValue) {
          const url = getUrl(val.id, val.module);
          const win = window.open(url, '_blank');
          win.focus();
        }
      }}
      className={cn({
        [classes.sourceEmptyText]: !hasValue,
      })}>
      {!!iconType && <>{SOURCE_ICON_MAPPING[iconType]}</>}
      <span>{!iconType && !val?.jobNo ? '/' : val?.jobNo || ''}</span>
    </span>
  );
};
const renderNormal = (val) => {
  return val || '/';
};
const renderType = (val) => {
  return val?.name || '/';
};
const countNormal = (showTooltip, tKey) => (val) => {
  if (showTooltip && val?.length > 0) {
    const items = val.map((obj) => {
      const idKey =
        tKey === 'products' ? 'itemNo' : tKey === 'suppliers' ? 'supplierId' : 'factoryId';
      return {
        content: [obj.name, obj[idKey]].filter((v) => !!v).join(' '),
        link:
          tKey === 'products'
            ? `/product/profile/${obj.id}`
            : tKey === 'suppliers'
            ? `/supplier-manager/detail/${obj.id}`
            : `/supplier-manager/factory-detail/${obj.id}`,
      };
    });
    return (
      <ATooltip
        dark
        placement="bottom"
        overlayClassName={classes.tooltipOverlay}
        title={
          <>
            Saved To
            <br />
            <div className={classes.tooltipLinks}>
              {items.map((item, index) => {
                return (
                  <React.Fragment key={index}>
                    <span
                      onClick={() => {
                        const win = window.open(item.link, '_blank');
                        win.focus();
                      }}>
                      {item.content}
                    </span>
                    {index < items.length - 1 && ', '}
                  </React.Fragment>
                );
              })}
            </div>
          </>
        }>
        <span>{val.length}</span>
      </ATooltip>
    );
  }
  return val?.length || 0;
};
const renderSize = (val, obj) => {
  if (obj?.isFolder) return '';
  return formatBytes(Number(val || 0));
};
const renderDate = (isReminder) => (val, obj) => {
  if (obj?.isFolder) return '';
  const today = getMomentDate('', true).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  const tMomentVal = getMomentDate(val);
  const isRed =
    !isReminder &&
    !!tMomentVal &&
    tMomentVal
      .clone()
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      .isBefore(today);
  const displayValue = getDateWithFormat(val, 'DD MMM YYYY') || '/';
  const showReminder = !!obj?.reminderType;
  const isReminderExpiry = obj?.reminderType === 'onExpiryDate';
  const isReminderReview = obj?.reminderType === 'onReviewDate';
  const isSent = (isReminderExpiry && !obj?.expiryDate) || (isReminderReview && !obj?.reviewDate);
  return (
    <span
      className={cn({
        [classes.red]: isRed,
      })}>
      <span>{displayValue}</span>
      {isReminder && showReminder && (
        <ATooltip
          dark
          placement="bottomRight"
          overlayClassName={classes.tooltipOverlay}
          title={
            <>
              Email Reminder {isSent ? 'Sent ' : ''}on{' '}
              {getDateWithFormat(obj.reminderDate, 'DD MMM YYYY') || '/'}
              <br />
              Note: {obj?.note || '/'}
            </>
          }>
          <span
            className={cn(classes.reminderIcon, {
              [classes.isSent]: isSent,
            })}>
            <ReminderSvg />
          </span>
        </ATooltip>
      )}
    </span>
  );
};

export const JobColDefs = [
  {
    title: T.translate('documentManagement.table.name'),
    dataIndex: 'name',
    width: '20%',
    render: renderDocumentName,
    className: classes.nameColumn,
    width: 450,
  },
  {
    title: T.translate('documentManagement.table.type'),
    dataIndex: 'type',
    width: '10%',
    render: renderType,
  },
  {
    title: T.translate('documentManagement.table.source'),
    dataIndex: 'source',
    width: '10%',
    render: renderSource,
    className: classes.sourceColumn,
  },
  {
    title: T.translate('documentManagement.table.reviewDate'),
    dataIndex: 'reviewDate',
    width: '10%',
    render: renderDate(),
  },
  {
    title: T.translate('documentManagement.table.expiryDate'),
    dataIndex: 'expiryDate',
    render: renderDate(),
  },
  {
    title: T.translate('documentManagement.table.product'),
    dataIndex: 'products',
    render: countNormal(true, 'products'),
  },
  {
    title: T.translate('documentManagement.table.supplier'),
    dataIndex: 'suppliers',
    render: countNormal(true, 'suppliers'),
  },
  {
    title: T.translate('documentManagement.table.factory'),
    dataIndex: 'factories',
    render: countNormal(true, 'factories'),
  },
  {
    title: T.translate('documentManagement.table.size'),
    dataIndex: 'size',
    render: renderSize,
  },
  {
    title: T.translate('documentManagement.table.uploadedDate'),
    dataIndex: 'uploadedDate',
    render: renderDate(true),
    className: classes.uploadedDateColumn,
  },
];
